// import React, { useRef, useState, useEffect } from "react";
// import { classNames } from "../../helpers";

// const Drawer = ({
//   isOpen,
//   onClose,
//   direction = "left",
//   children,
//   width = "256px",
// }) => {
//   const drawerRef = useRef(null);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (drawerRef.current && !drawerRef.current.contains(event.target)) {
//         onClose();
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [onClose]);

//   return (
//     <div
//       className={classNames(
//         `fixed top-0 h-screen bg-white shadow-lg transition-["width"_"transform"] duration-300 ease-in-out z-50 px-5 py-10 space-y-4`,
//         isOpen
//           ? "translate-x-0"
//           : direction === "left"
//           ? "-translate-x-full !w-0"
//           : direction === "right" && "translate-x-full !w-0",
//         direction === "left" ? "left-0" : direction === "right" && "right-0"
//       )}
//       style={{ width }}
//       ref={drawerRef}
//     >
//       <div
//         className={classNames(
//           `transition-["display"] duration-500 ease-in-out h-full`,
//           isOpen ? "block" : "hidden"
//         )}
//       >
//         {children}
//       </div>
//     </div>
//   );
// };

// export default Drawer;

import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { classNames } from "../../helpers/classNames";
import Button from "../form/Button";
import React, { cloneElement, useEffect, useRef, useState } from "react";
import { elementResize } from "../../helpers";

function isReactElement(value) {
  return React.isValidElement(value);
}

export default function Drawer({
  actualSize,
  size = "xl4",
  open = false,
  onClose = () => {},
  header,
  title = "Modal title",
  subtitle = "Modal subtitle",
  children,
  animationDuration = 0.2,
  headerClass,
  openFrom = "right",
  drawerBodyClass,
  closeBtn = true,
}) {
  const [sizeValue, setSizeValue] = useState(size);

  useEffect(() => {
    setSizeValue(size);
  }, [size]);

  const drawerSize = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    xl2: "max-w-2xl",
    xl3: "max-w-3xl",
    xl4: "max-w-4xl",
    xl5: "max-w-5xl",
    xl6: "max-w-6xl",
    xl7: "max-w-7xl",
    xl8: "max-w-8xl",
    xl9: "max-w-9xl",
    full: "max-w-full !rounded-none",
  };

  const getMotionProps = () => {
    if (sizeValue === "full") {
      return {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
      };
    }

    switch (openFrom) {
      case "left":
        return {
          initial: { x: "-100%" },
          animate: { x: 0 },
          exit: { x: "-100%" },
        };
      case "top":
        return {
          initial: { y: "-100%" },
          animate: { y: 0 },
          exit: { y: "-100%" },
        };
      case "bottom":
        return {
          initial: { y: "100%" },
          animate: { y: 0 },
          exit: { y: "100%" },
        };
      case "right":
      default:
        return {
          initial: { x: "100%" },
          animate: { x: 0 },
          exit: { x: "100%" },
        };
    }
  };

  const positionClasses = () => {
    if (sizeValue === "full") return "justify-center items-center"; // center popup

    switch (openFrom) {
      case "left":
        return "justify-start";
      case "right":
        return "justify-end";
      case "top":
        return "items-start";
      case "bottom":
        return "items-end";
      default:
        return "justify-end";
    }
  };

  const isHorizontal = openFrom === "left" || openFrom === "right";

  const renderChildrenWithProps = () =>
    React.Children.map(children, (child) =>
      isReactElement(child) && typeof child.type !== "string"
        ? cloneElement(child, {
            actualSize,
            sizeValue,
            open,
            onClose,
            setSizeValue,
            header,
            heading: title,
            subtitle,
            children,
            animationDuration,
            headerClass,
            openFrom,
            drawerBodyClass,
            closeBtn,
          })
        : child
    );

  const renderHeaderWithProps = () =>
    React.Children.map(isReactElement(header) ? header : <></>, (child) =>
      isReactElement(child) && typeof child.type !== "string"
        ? cloneElement(child, {
            actualSize,
            sizeValue,
            open,
            onClose,
            setSizeValue,
            header,
            heading: title,
            subtitle,
            children,
            animationDuration,
            headerClass,
            openFrom,
            drawerBodyClass,
            closeBtn,
          })
        : child
    );

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          as="div"
          className={classNames(
            "relative",
            sizeValue === "full" || openFrom === "top" || openFrom === "bottom"
              ? "z-90"
              : "z-50"
          )}
          onClose={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            style={{ transformOrigin: "bottom center" }} // simulates dock location
            className="fixed inset-0 bg-black/30 backdrop-blur-sm backdrop-saturate-150"
            aria-hidden="true"
          />

          <div className={classNames("fixed inset-0 flex", positionClasses())}>
            <motion.div
              drag={isHorizontal ? "x" : "y"}
              dragConstraints={{ left: 0, right: 0 }} // optional
              onDragEnd={(e, info) => {
                const isTextareaResize =
                  e.target?.tagName === "TEXTAREA" ||
                  e.target?.closest("textarea");
                // console.log(isTextareaResize);
                if (isTextareaResize) return; // don't close on textarea resize
                if (
                  Math.abs(info.offset.x) > 400 ||
                  Math.abs(info.offset.y) > 400
                ) {
                  onClose();
                }
              }}
              {...getMotionProps()}
              dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
              transition={{ duration: animationDuration }}
              className={classNames(
                "bg-white shadow-2xl overflow-hidden ring-1 ring-black/5",

                // "bg-white shadow-2xl overflow-hidden ring-1 ring-black/5",
                isHorizontal ? "h-full w-full rounded-l-xl" : "w-full",
                !isHorizontal ? "w-full" : "",
                openFrom === "top" || openFrom === "bottom"
                  ? drawerSize["full"]
                  : drawerSize[sizeValue]
              )}
            >
              <DialogPanel
                as={motion.div}
                {...getMotionProps()}
                transition={{ type: "tween" }}
                className="h-full w-full"
              >
                <div className="w-full h-full flex flex-col">
                  {header !== false && (
                    <div className="relative flex-grow-0">
                      {header ? (
                        renderHeaderWithProps()
                      ) : (
                        <div className="p-6 py-4 bg-gray-100 border-b border-gray-300">
                          <div className={classNames("space-y-1", headerClass)}>
                            {title && (
                              <DialogTitle className="font-bold text-xl">
                                {title}
                              </DialogTitle>
                            )}
                            {subtitle && (
                              <Description className="text-sm text-gray-400">
                                {subtitle}
                              </Description>
                            )}
                          </div>
                        </div>
                      )}
                      {closeBtn && (
                        <Button
                          buttonIconPosition="left"
                          buttonIcon="fa-regular fa-xmark"
                          // buttonClasses="absolute top-3 right-1 p-2 h-8 w-8"
                          buttonClasses="absolute top-3 right-1 p-2 h-8 w-8 transition-all hover:bg-red-200 rounded-full"
                          buttonFunction={onClose}
                        />
                      )}
                    </div>
                  )}
                  <div
                    className={classNames(
                      "flex-grow w-full p-6 overflow-y-auto scroll-smooth",
                      header ? "max-h-[calc(100%-85px)]" : "max-h-full",
                      drawerBodyClass
                    )}
                  >
                    {renderChildrenWithProps()}
                  </div>
                </div>
              </DialogPanel>
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
